import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const QASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      id="qa" 
      className="py-24 md:py-32 relative" 
      ref={ref} 
      data-testid="qa-section"
      style={{
        background: 'linear-gradient(180deg, #050505 0%, #0a1225 50%, #050505 100%)'
      }}
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.2em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Q&A
          </h2>
          <div className="w-24 h-[1px] bg-[#c9a227]/50 mx-auto" />
        </motion.div>

        <div className="space-y-12">
          {/* Q1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              What is Celestial Minds?
            </h3>
            <p className="text-white/70 leading-relaxed">
              Celestial Minds™ is a sovereign idea foundry that originates and architects next-generation systems across technology, finance, infrastructure, intelligence, and human experience. Operating upstream of markets and institutions, Celestial Minds designs licensable frameworks, platforms, and future architectures—transforming first-principles insight into deployable, investor-grade realities. We work selectively with governments, investors, and enterprises, not to advise on what exists, but to author what must exist next.
            </p>
          </motion.div>

          {/* Q2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              Why "Celestial"?
            </h3>
            <p className="text-white/70 leading-relaxed">
              Because our work extends beyond the known limits of human thought. We design for the civilisation humanity has not yet reached — but inevitably will.
            </p>
          </motion.div>

          {/* Q3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              What makes Celestial Minds different from innovation labs?
            </h3>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>Innovation labs improve what exists.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>We design what comes next.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>We do not iterate.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>We architect futures.</span>
              </li>
            </ul>
          </motion.div>

          {/* Q4 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              Who works with Celestial Minds?
            </h3>
            <p className="text-white/70 leading-relaxed">
              Visionaries, sovereign investors, innovators, governments, founders, creators, futurists, and strategic institutions capable of influencing national or global-scale change.
            </p>
          </motion.div>

          {/* Q5 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              Can anyone join?
            </h3>
            <p className="text-white font-medium text-lg mb-4">No.</p>
            <p className="text-white/70 leading-relaxed">
              Only entities aligned with elevated thinking, integrity, and frontier-level vision may collaborate with Celestial Minds.
            </p>
            <p className="text-[#c9a227] italic mt-4">
              We are selective — because the future demands precision.
            </p>
          </motion.div>

          {/* Q6 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              Is Celestial Minds™ a think tank?
            </h3>
            <p className="text-white/70 leading-relaxed">
              No. While some of our work may resemble think-tank depth, Celestial Minds™ is fundamentally an idea foundry focused on originating and architecting new systems, not policy commentary.
            </p>
          </motion.div>

          {/* Q7 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              Do you accept unsolicited proposals or pitches?
            </h3>
            <p className="text-white/70 leading-relaxed">
              No. Celestial Minds™ operates on a selective, invitation-based engagement model.
            </p>
          </motion.div>

          {/* Q8 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl md:text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              Are you raising capital publicly?
            </h3>
            <p className="text-white/70 leading-relaxed mb-4">
              No. Celestial Minds™ does not conduct public fundraising or offer securities to the public.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              Celestial Minds™ accepts voluntary contributions intended solely to support independent research and development activities. Such contributions do not confer equity, ownership, governance rights, or expectations of financial return.
            </p>
            <p className="text-white/70 leading-relaxed">
              Any investment discussions, where appropriate, occur privately and only after strategic alignment and confidential review.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QASection;
